import { useEffect } from 'react';
import { debounce } from 'lodash';
import { type MessagePayload } from 'firebase/messaging';

import { onMessage, getMessaging } from '@firebase/messaging';
import {
    type IGetConversationsResponse,
    apiClient,
    useSubscribeToPushNotifications,
    useUserSettings,
    useAchievements,
} from 'src/services/api';
import {useNavigate} from 'react-router-dom';

import {
    useUpdateConversations,
} from 'src/services/push-notifications/use-update-conversations';
import { redirectToLandlordLoginV2 } from 'src/api';
import { fbApp, getPushToken } from 'src/services/push-notifications/firebase';
import {getPermission} from 'src/services/push-notifications/utils';
import { wait } from 'src/utils/wait';

enum PushNotificationType {
    NEW_LEAD_MESSAGE = 'NEW_LEAD_MESSAGE',
    CONVERSATIONS_UPDATE = 'CONVERSATIONS_UPDATE',
    NEW_ACHIEVEMENT_EARNED = 'NEW_ACHIEVEMENT_EARNED'
}


export const usePushNotifications = () => {
    const updateConversations = useUpdateConversations();
    const { data: userSettings } = useUserSettings();
    const { refetch: refetchAchievements } = useAchievements();

    const navigate = useNavigate();

    useEffect(() => {
        if (!navigator || !navigator.serviceWorker) {
            return;
        }

        const messaging = getMessaging(fbApp);

        const handleAchievementMessage = async () => {
            // Only show confetti if achievements are enabled
            if (userSettings?.is_achievements_enabled !== false) {
                await wait(2000);
                await refetchAchievements();
            }
        };

        const handleMessage = debounce(async (payload: MessagePayload) => {
            const data = payload.data || {};

            const notificationTitle = payload.notification?.title || 'New message from lead';
            const notificationBody = payload.notification?.body || 'You have a new message from lead.';
            const leadUuid = data.lead_uuid;
            // show only if notification is enabled in user settings
            if (userSettings?.notifications.new_message !== 'off') {
                const notification = new Notification(notificationTitle, { body: notificationBody, icon: '/img/reffie-logos/brain-transparent-32.png' });

                notification.onclick = () => {
                    window?.focus();
                    if (leadUuid) {
                        navigate(`/landlord/inner/messages/from-lead?lead_uuid=${leadUuid}`);
                    } else {
                        navigate('/landlord/inner/root');
                    }
                    notification.close();
                };
            }
            const searchParams = new URLSearchParams();
            const since = new Date();
            since.setMinutes(since.getMinutes() - 5);
            searchParams.append('since', since.toISOString().slice(0, -1));
            try {
                const res = await apiClient('api/landlord/conversations', {
                    searchParams,
                }).json<IGetConversationsResponse>();

                updateConversations(res.conversations);
            } catch (error: any) {
                if (error?.response?.status === 401) {
                    redirectToLandlordLoginV2();
                } else {
                    console.error('Failed to fetch conversations:', error);
                }
            }
        }, 2000, { leading: true, trailing: true });
        return onMessage(messaging, async (payload) => {
            const notificationType = payload?.data?.type || {};

            if (notificationType === PushNotificationType.NEW_LEAD_MESSAGE || notificationType === PushNotificationType.CONVERSATIONS_UPDATE) {
                await handleMessage(payload);
            } else if (notificationType === PushNotificationType.NEW_ACHIEVEMENT_EARNED) {
                await handleAchievementMessage();
            }
        });
    }, [updateConversations, userSettings]);

    const { mutate: subscribeToPushNotifications } = useSubscribeToPushNotifications();

    useEffect(() => {
        const subscribe = async () => {
            try {
                const permission = await getPermission();
                if (permission !== 'granted') {
                    console.log('Push notifications permission not granted:', permission);
                    return;
                }
                const token = await getPushToken();
                if (token) {
                    subscribeToPushNotifications({
                        token
                    });
                }
            } catch (error) {
                console.error('Error creating push subscription:', error);
                throw error;
            }
        };

        subscribe();
    }, []);
};

export const usePushSubscription = () => {
    const { mutateAsync: subscribeToPushNotifications } = useSubscribeToPushNotifications();

    const createPushSubscription = async (): Promise<boolean> => {
        try {
            const token = await getPushToken();
            if (token) {
                await subscribeToPushNotifications({ token });
                return true;
            }
            return false;
        } catch(error) {
            console.error('usePushSubscription error', error);
            return false;
        }
    };

    return { createPushSubscription };
};
